<template>
  <b-card no-body>
    <b-card-body class="p-0" id="cost-prices-card-body">
      <cost-prices-unit-filter
        ref="costPricesFilter"
        :values="filterValues"
        :filter-submitting="isLoading"
        @filter="setFilter"
      />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :is-loading.sync="isLoading"
        :total-rows="totalRecords"
        :rows="rows"
        :columns="getColumns"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <table-no-result-found slot="emptystate" />
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'importId'">
            {{ props.row.importId ? props.row.importId : '-' }}
          </span>
          <span v-else-if="props.column.field === 'transportDirection'">
            {{ props.row.transportDirection ? props.row.transportDirection : '-' }}
          </span>
          <span v-else-if="props.column.field === 'transportType'">
            {{ props.row.transportType ? props.row.transportType : '-' }}
          </span>
          <span v-else-if="props.column.field === 'name'">
            {{ props.row.name ? props.row.name : '-' }}
          </span>
          <span v-else-if="props.column.field === 'packageType'">
            {{ props.row.packageType ? props.row.packageType : '-' }}
          </span>
          <span v-else-if="props.column.field === 'packageTypeValue'">
            {{ props.row.packageTypeDescription ? props.row.packageTypeDescription : '-' }}
          </span>
          <span v-else-if="props.column.field === 'validity_dates'">
            <div class="d-flex flex-column">
              <div class="d-flex align-items-center gap-1">
                <span class="font-weight-medium">
                  {{ $dayjs(props.row.activeAt).format('DD.MM.YYYY HH:mm') }}
                </span>
              </div>
              <span class="font-weight-medium">
                {{ $dayjs(props.row.activeUntil).format('DD.MM.YYYY HH:mm') }}
              </span>
            </div>
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge :variant="getStatusVariant(props.row.status)" class="text-capitalize">
              {{ props.row.statusText ? props.row.statusText : '-' }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <router-link
                :to="{
                  name: 'cost-prices-unit-prices-detail',
                  params: { importId: props.row.importId }
                }"
              >
                <b-button
                  :id="`show-cost-price-button-${props.row.importId}`"
                  variant="primary"
                  :disabled="props.row.status !== 2"
                  type="button"
                  size="sm"
                >
                  <feather-icon icon="EyeIcon" size="15" />
                  <b-tooltip
                    triggers="hover"
                    :target="`show-cost-price-button-${props.row.importId}`"
                    :title="t('Detail')"
                    :delay="{ show: 0, hide: 0 }"
                    boundary="viewport"
                  />
                </b-button>
              </router-link>
              <b-button variant="danger" size="sm" @click="deleteCostUnitPrice(props.row.importId)">
                <feather-icon icon="TrashIcon" size="15" />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BTooltip, BButton, BBadge } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import CostPricesUnitFilter from './CostPricesUnitFilter.vue';

export default {
  components: {
    VueGoodTable,
    BCard,
    BCardBody,
    BTooltip,
    BButton,
    BBadge,
    CostPricesUnitFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      totalRecords: 0,
      rows: [],
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        transportDirection: '',
        transportType: '',
        providerId: '',
        packageType: '',
        packageTypeValue: '',
        activeAt: '',
        activeUntil: ''
      },
      defaultFilterValues: {
        transportDirection: '',
        transportType: '',
        providerId: '',
        packageType: '',
        packageTypeValue: '',
        activeAt: '',
        activeUntil: ''
      },
      packageTypes: [],
      packagingTypes: [],
      providerPriceImportStatusOptions: []
    };
  },
  computed: {
    getColumns() {
      return [
        {
          label: this.$t('ID'),
          field: 'importId'
        },
        {
          label: this.$t('Provider'),
          field: 'name'
        },
        {
          label: this.$t('Transport Direction'),
          field: 'transportDirection'
        },
        {
          label: this.$t('Transport Type'),
          field: 'transportType'
        },
        {
          label: this.$t('Packaging Type'),
          field: 'packageType'
        },
        {
          label: this.$t('Package Type'),
          field: 'packageTypeValue'
        },
        {
          label: this.$t('Validity Dates'),
          field: 'validity_dates'
        },
        {
          label: this.$t('Import Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  created() {
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },
    getStatusVariant(status) {
      switch (status) {
        case 0:
          return 'warning';
        case 1:
          return 'info';
        case 2:
          return 'success';
        case 3:
          return 'danger';
        default:
          return 'secondary';
      }
    },
    async loadItems() {
      this.isLoading = true;

      try {
        const requestData = {
          ...this.filter,
          ...this.getFilteredObject(this.filterValues)
        };

        if (!this.packageTypes.length || !this.packagingTypes.length) {
          const [
            packageTypesResponse,
            packagingTypesResponse,
            transportDirectionsResponse,
            transportTypesResponse,
            providerPriceImportStatusResponse
          ] = await Promise.all([
            useService.constantService.getConsignmentPackageType(),
            useService.getProviderRegionPricePackageType(),
            useService.constantService.getTransportDirection(),
            useService.constantService.getTransportType(),
            useService.constantService.getProviderPriceImportStatus()
          ]);

          this.packageTypes = packageTypesResponse.data;
          this.packagingTypes = packagingTypesResponse.data;
          this.transportDirections = transportDirectionsResponse.data;
          this.transportTypes = transportTypesResponse.data;
          this.providerPriceImportStatusOptions = providerPriceImportStatusResponse.data;
        }

        const response = await useService.pricingService.getUnitPrices(requestData);
        this.rows = response.data.map(item => ({
          ...item,
          packageType: item.packageType
            ? this.packagingTypes.find(
                type => type.value.toString() === item.packageType.toString()
              )?.info || item.packageType
            : '-',
          packageTypeValue: item.packageTypeValue
            ? this.packageTypes.find(
                type => type.value.toString() === item.packageTypeValue.toString()
              )?.info || item.packageTypeValue
            : '-',
          transportDirection: item.transportDirection
            ? this.transportDirections.find(
                type => type.value.toString() === item.transportDirection.toString()
              )?.info || item.transportDirection
            : '-',
          transportType: item.transportType
            ? this.transportTypes.find(
                type => type.value.toString() === item.transportType.toString()
              )?.info || item.transportType
            : '-',
          statusText:
            this.providerPriceImportStatusOptions.find(
              status => status.value.toString() === item.status.toString()
            )?.info || item.status
        }));
        this.totalRecords = response.meta.total;
      } catch (error) {
        this.showErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },
    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },

    editCostPrice(id) {
      this.$router.push(`/cost-prices/${id}/edit`);
    },

    deleteCostUnitPrice(importId) {
      this.$confirm(this.$t('Are you sure you want to delete this cost unit price?'), {
        confirmButtonText: this.$t('Delete'),
        cancelButtonText: this.$t('Cancel'),
        type: 'warning'
      }).then(async result => {
        this.isLoading = true;
        if (result.value) {
          try {
            await useService.pricingService.deleteUnitPrice(importId);
            this.loadItems();
            this.showSuccessMessage(this.$t('Cost unit price deleted successfully'));
          } catch (messages) {
            this.isLoading = false;
            if (typeof messages === 'object' && messages && messages.length) {
              messages.forEach(item => {
                this.showErrorMessage(item.message);
              });
            }
          }
        }
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
